import { computed, effectScope, type ComputedRef } from 'vue';
import type { AccountConfigHandle } from '../config/accountConfig';
import { dictionaryEffectiveDocument } from '../config/dictionaryLayer';
import {
  spellDictionaryWords,
  spellNeverSuggestWords,
  type SpellCheckConfig,
} from '../config/spellCheck';
import { dictionaryFold } from '../text/dictionaryFold';

/**
 * The folds of what the effective `spell_check` says about words (I-3024): the
 * never-suggest words of every language and layer, and the words of the
 * personal dictionary (own, company and tenant). One computed pair per config
 * entry, in a scope of its own, so a reader that unmounts never takes the
 * sets away from the others. A design carries no dictionary: the sets are
 * built from the layers without the design layers (`dictionaryLayers`). While
 * the layers are not read yet, master's effective document is all there is.
 */
export interface ResolvedDictionaryFolds {
  /** Folds of every `never_suggest` word, any language. */
  never: ComputedRef<ReadonlySet<string>>;
  /** Folds of every word of `personal_dictionary`. */
  personal: ComputedRef<ReadonlySet<string>>;
}

const shared = new WeakMap<object, ResolvedDictionaryFolds>();

function foldSet(words: readonly string[]): ReadonlySet<string> {
  const folds = new Set<string>();
  for (const word of words) {
    const key = dictionaryFold(word);
    if (key) folds.add(key);
  }
  return folds;
}

export function resolvedDictionaryFolds(
  handle: Pick<AccountConfigHandle<SpellCheckConfig>, 'effective'> & Partial<Pick<AccountConfigHandle<SpellCheckConfig>, 'layers'>>,
): ResolvedDictionaryFolds {
  const known = shared.get(handle.effective);
  if (known) return known;
  const folds = effectScope(true).run<ResolvedDictionaryFolds>(() => {
    const document = computed(
      () => dictionaryEffectiveDocument(handle.effective.value, handle.layers?.value) as SpellCheckConfig | undefined,
    );
    return {
      never: computed(() => foldSet(spellNeverSuggestWords(document.value))),
      personal: computed(() => foldSet(spellDictionaryWords(document.value))),
    };
  })!;
  shared.set(handle.effective, folds);
  return folds;
}
